import { Slide } from "@board/cloud-library/components/carousel";

export interface DemoAssetViewModel extends DemoTileViewModel {
  owner: string;
  creationDate: string;
  updateBy: string;
  lastUpdate: string;
  demoAssetSize: number;
  draft: boolean;
  default: boolean;
  aclType: DemoAssetAclEnum;
  categoryId?: number;
  category?: AssetCategoryViewModel;
}

export interface DemoTileViewModel {
  id: number;
  name: string;
  description: string;
  ramConsumption: number;
  diskConsumption: number;
  suggestedDemos: SuggestedDemo[];
  useCases: number[];
  processes: number[];
  domains: number[];
  functionalities: number[];
  default: boolean;
  industries?: IndustryViewModel[];
  newsCategoryName?: string;
  newsCategoryColor?: string;
  assetCategory?: {
    id: number;
    name: string;
    color: string;
    icon: string;
  };
}

export enum SuggestedBehavior {
  suggested = 0,
  default
}

export interface SuggestedDemo {
  suggestedId: number;
  behavior: SuggestedBehavior;
  suggestedRamConsumption: number;
  suggestedDiskConsumption: number;
}

export interface DemoAssetFormViewModel {
  id: number;
  name: string;
  description: string;
  envVars: EnvVarModel[];
  version: string;
  modules: ModuleFormModel[];
  defaultImageFileName?: string;
  hoverImageFileName?: string;
  demoSourceFileName?: string;
  agentsConfigFileName?: string;
  ramConsumption: number;
  diskConsumption: number;
  demoSize?: number;
  industries?: IndustryViewModel[];
  materials: MaterialModuleModel[];
  suggestedDemos?: SuggestedDemo[];
  lastUpdate?: Date;
  draft: boolean;
  default: boolean;
  aclType: DemoAssetAclEnum;
  aclSelectedGroups: string[];
  categoryId?: number;
}

export enum DemoAssetAclEnum {
  Everyone = 0,
  OnlyBoard = 1,
  SelectedGroups = 2
}

export const DemoAssetAclLookup = [
  { value: DemoAssetAclEnum.Everyone, text: "Everyone" },
  { value: DemoAssetAclEnum.OnlyBoard, text: "Only Board" },
  { value: DemoAssetAclEnum.SelectedGroups, text: "Selected groups" }
];

export interface IndustryViewModel {
  id: number;
  name: string;
}

export interface GroupLimitViewModel {
  companyName: string;
  limit: number;
  isBoardGroup: boolean;
}

export interface DemoAssetFormViewModelWithFile extends DemoAssetFormViewModel {
  demoSource: File[] | null;
  defaultImage: File[] | null;
  hoveredImage: File[] | null;
  slides: Slide[];
  agentsConfig: File[] | null;
}

export interface EnvVarModel {
  key: string;
  value: string;
}

export interface ModuleFormModel {
  id: number;
  name: string;
  description: string;
  useCases: number[];
  functionalities: number[];
  demoAssetId: number;
  order: number;
}

export interface FunctionalityViewModel {
  id: number;
  name: string;
  groups: GroupViewModel[];
}

export interface DomainViewModel {
  id: number;
  name: string;
}

export interface ProcessViewModel {
  id: number;
  name: string;
  domainId: number;
}

export interface UseCaseViewModel {
  id: number;
  name: string;
  processId: number;
  domainId: number;
}

export interface UseCaseFormViewModel {
  id: number;
  name: string;
  processId: number;
  processName: string;
  domainId: number;
  domainName: string;
}

export interface DemoFormViewModel {
  functionalities: FunctionalityViewModel[];
  domains: DomainViewModel[];
  processes: ProcessViewModel[];
  useCases: UseCaseViewModel[];
  groups: GroupViewModel[];
  industries: IndustryViewModel[];
  accounts: AccountViewModel[];
  limits: {
    maxRamConsumption: number;
    maxDiskConsumption: number;
  };
  categories: AssetCategoryViewModel[];
}

export interface AccountViewModel {
  accountName: string;
  accountId: string;
  isBoardGroup: boolean;
  partnerTier: string;
}

export interface FilterViewModel {
  domains: DomainViewModel[];
  processes: ProcessViewModel[];
  useCases: UseCaseViewModel[];
  functionalities: FunctionalityViewModel[];
  industries: IndustryViewModel[];
  categories: AssetCategoryViewModel[];
  limits: {
    maxRamConsumption: number;
    maxDiskConsumption: number;
  };
}

export interface DemoAssetMediaModel {
  demoAssetId: number;
  defaultImage: string;
  hoverImage: string;
}

export interface DemoAssetCarouselModel {
  demoAssetId: number;
  images: CarouselImage[];
}

export interface CarouselImage {
  imagePath: string;
  imageSrc: string;
}

export interface GroupViewModel {
  id: number;
  name: string;
}

export interface MaterialModuleModel {
  type: MaterialType;
  name: string;
  url: string;
}

export enum MaterialType {
  pdf = 0,
  video
}

export interface DemoTemplateViewModel {
  id: number;
  name: string;
  description: string;
  version: string;
  owner?: string;
  creationDate?: Date;
  updateBy?: string;
  updateDate?: Date;
  vmImageId: number;
  estimatedTime: number;
  isEnabled: boolean;
  group?: string;
  demoAssets: number[];
  relatedDemoAssets?: DemoAssetViewModel[];
}

export interface TemplateFormViewModel {
  vmImages: VmImage[];
}

export interface VmImage {
  id: number;
  name: string;
  version: string;
  latest: boolean;
}
export interface DemoAssetPopupViewModel {
  id: number;
  name: string;
  description: string;
  lastUpdate: string;
  version: string;
  industries: string[];
  materials: MaterialModuleModel[];
  modules: DemoModulePopupViewModel[];
}

export interface DemoModulePopupViewModel {
  id: number;
  name: string;
  description: string;
  useCases: string[];
  functionalities: string[];
}

export interface DemoAssetTemplateViewModel {
  templateId: number;
  templateName: string;
}

export interface DemoAssetNameViewModel {
  id: number;
  name: string;
  draft: boolean;
  default: boolean;
}

export interface AssetCategoryViewModel {
  id: number;
  name: string;
  color: string;
  icon: string;
}
